'use client';

import Image from 'next/image';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Shield, Code2, Brain, Phone, Sparkles, ShieldCheck } from 'lucide-react';
import { EASE_OUT, EASE_CUSTOM } from '@/lib/animations'

const stats = [
    { value: '40+', label: 'Preguntas por diagnóstico' },
    { value: '< 48h', label: 'Propuesta personalizada' },
    { value: '100%', label: 'Trato directo con el fundador' },
];

const teams = [
    {
        icon: Phone,
        title: 'Equipo Comercial',
        desc: 'Prospección, cualificación y seguimiento del pipeline. Garantizamos que cada propuesta llega al cliente adecuado en el momento adecuado.',
        color: 'blue',
    },
    {
        icon: Code2,
        title: 'Equipo de Desarrollo',
        desc: 'Arquitectura, backend, frontend, automatizaciones e integraciones de IA. Implementamos internamente cada solución que el diagnóstico recomienda.',
        color: 'indigo',
    },
    {
        icon: Sparkles,
        title: 'Equipo de Diseño',
        desc: 'Interfaz, experiencia de usuario e identidad visual. Diseñamos productos que comunican confianza y se usan sin fricción.',
        color: 'violet',
    },
    {
        icon: ShieldCheck,
        title: 'Soporte Técnico',
        desc: 'Mantenimiento, formación al equipo del cliente y mejoras continuas. Acompañamos al cliente más allá de la entrega del proyecto.',
        color: 'emerald',
    },
];

const teamColorMap = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-100', icon: 'text-blue-600' },
    indigo: { bg: 'bg-indigo-50', border: 'border-indigo-100', icon: 'text-indigo-600' },
    violet: { bg: 'bg-violet-50', border: 'border-violet-100', icon: 'text-violet-600' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-100', icon: 'text-emerald-600' },
};

export default function Nosotros() {
    return (
        <section id="nosotros" className="py-20 md:py-28 relative z-10">
            <div className="max-w-5xl mx-auto px-6 lg:px-8">

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.9, ease: EASE_CUSTOM }}
                    className="text-center mb-14"
                >
                    <div className="text-xs font-mono tracking-[0.3em] text-slate-400 uppercase mb-4">Sobre nosotros</div>
                    <h2 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-4">
                        Tu asesor es quien implementa.{' '}
                        <span className="text-slate-400 font-light">Siempre.</span>
                    </h2>
                </motion.div>

                {/* ── Tedi — Founder spotlight (with photo) ── */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.8, ease: EASE_CUSTOM }}
                    className="glass-card rounded-3xl p-5 sm:p-6 md:p-8 mb-6 overflow-hidden"
                >
                    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_1.4fr] gap-6 md:gap-8 md:items-stretch">
                        {/* Photo column — explicit width/height for iOS Safari compatibility */}
                        <div className="relative rounded-2xl overflow-hidden bg-slate-100 ring-1 ring-slate-200/60 md:h-full md:min-h-[440px] shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
                            <Image
                                src="/images/tedi_en_accion.jpg"
                                alt="Teodor Chavdarov, fundador de Innovathera Technology, en una sesión de trabajo: videollamada con cliente, código en pantalla y pizarra con métricas comerciales reales"
                                width={1200}
                                height={1381}
                                sizes="(min-width: 768px) 40vw, 100vw"
                                priority={false}
                                className="block w-full h-auto md:absolute md:inset-0 md:w-full md:h-full md:object-cover md:object-center"
                            />
                            {/* Subtle gradient overlay for legibility on the bottom badge */}
                            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-slate-900/60 via-slate-900/15 to-transparent" />
                            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] font-mono tracking-[0.18em] uppercase text-slate-700 shadow-sm">
                                    <span className="relative flex h-1.5 w-1.5">
                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-75" />
                                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-blue-600" />
                                    </span>
                                    En operación
                                </span>
                                <span className="text-[10px] font-mono tracking-wider text-white/90 drop-shadow-sm">Madrid · 2026</span>
                            </div>
                        </div>

                        {/* Bio column */}
                        <div className="flex flex-col gap-3.5 min-w-0">
                            <div className="flex flex-wrap items-center gap-2.5">
                                <span className="font-bold text-slate-900 text-lg sm:text-xl tracking-tight">Teodor Chavdarov</span>
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-600 text-white text-[11px] font-bold tracking-wide uppercase shadow-sm">
                                    Fundador &middot; Asesor
                                </span>
                            </div>
                            <span className="block text-xs font-mono tracking-widest text-slate-400 uppercase">
                                Analista de Negocio &amp; Ingeniero de Software
                            </span>
                            <p className="text-slate-600 text-[15px] leading-relaxed">
                                Ingeniero de software con M&aacute;ster en Ciberseguridad y formaci&oacute;n previa como
                                asesor financiero. Antes de tocar el c&oacute;digo, entiendo c&oacute;mo se gana y se pierde
                                el dinero en tu negocio. Dise&ntilde;o la arquitectura de la soluci&oacute;n, dirijo al
                                equipo comercial y al equipo de desarrolladores, y reviso cada entrega personalmente.
                            </p>
                            <p className="text-slate-600 text-[15px] leading-relaxed">
                                Cada negocio trabaja distinto, usa herramientas distintas y tiene cuellos de botella
                                distintos. Mi trabajo es entender esas diferencias y actuar en consecuencia &mdash;
                                sin paquetes cerrados ni propuestas gen&eacute;ricas.
                            </p>
                            <div className="flex flex-wrap gap-2 mt-1">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100">
                                    <Code2 className="w-3 h-3" /> An&aacute;lisis de Negocio
                                </span>
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium border border-indigo-100">
                                    <Shield className="w-3 h-3" /> Ciberseguridad
                                </span>
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-50 text-violet-700 text-xs font-medium border border-violet-100">
                                    <Brain className="w-3 h-3" /> Arquitectura Full-Stack
                                </span>
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-100">
                                    <Phone className="w-3 h-3" /> Direcci&oacute;n Comercial
                                </span>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* ── Team photo — real picture of the team in action ── */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.8, delay: 0.05, ease: EASE_CUSTOM }}
                    className="glass-card rounded-3xl overflow-hidden mb-6 relative"
                >
                    <div className="relative w-full aspect-[4/3] sm:aspect-[3/2] md:aspect-[16/10] bg-slate-100">
                        <Image
                            src="/images/equipo.jpg"
                            alt="El equipo de Innovathera Technology en una sesión interna de trabajo en la oficina de Madrid"
                            fill
                            sizes="(min-width: 1024px) 960px, 100vw"
                            className="object-cover"
                            style={{ objectPosition: 'center 28%' }}
                            priority={false}
                        />
                        {/* Overlay gradiente para legibilidad del texto */}
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/20 to-transparent pointer-events-none" />
                        {/* Caption */}
                        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
                            <div className="flex items-start sm:items-end justify-between gap-4 flex-col sm:flex-row">
                                <div className="text-white max-w-2xl">
                                    <p className="text-[10px] font-mono tracking-[0.3em] uppercase text-blue-200 mb-2">El equipo · 2026</p>
                                    <p className="text-base sm:text-xl font-bold leading-snug drop-shadow-sm">
                                        Una estructura que se reúne, decide y entrega bajo el mismo techo.
                                    </p>
                                </div>
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/95 backdrop-blur text-[10px] font-mono tracking-[0.2em] uppercase text-slate-700 shadow-sm whitespace-nowrap">
                                    Oficina Madrid
                                </span>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* ── Team — explicit breakdown ── */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.8, delay: 0.1, ease: EASE_CUSTOM }}
                    className="glass-card rounded-3xl p-6 sm:p-8 mb-10"
                >
                    <div className="flex items-baseline justify-between flex-wrap gap-3 mb-5">
                        <div>
                            <p className="text-xs font-mono tracking-[0.25em] text-slate-400 uppercase mb-1.5">Estructura del equipo</p>
                            <h3 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900">
                                Cuatro áreas especializadas.{' '}
                                <span className="text-slate-400 font-light">Una única dirección.</span>
                            </h3>
                        </div>
                        <span className="text-[11px] font-mono tracking-widest text-slate-400 uppercase">4 áreas · 1 dirección</span>
                    </div>
                    <p className="text-slate-500 font-light text-[15px] leading-relaxed mb-6 max-w-2xl">
                        El diagnóstico lo realiza Teodor personalmente. La ejecución la respaldan equipos especializados
                        bajo su dirección directa. Combinamos así la agilidad de una estructura compacta con la solidez
                        de una organización completa.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {teams.map((t) => {
                            const c = teamColorMap[t.color as keyof typeof teamColorMap];
                            const Icon = t.icon;
                            return (
                                <div
                                    key={t.title}
                                    className="rounded-2xl border border-slate-200/60 bg-white/55 backdrop-blur-sm p-5 flex gap-4 items-start hover:bg-white/80 hover:border-slate-300/70 transition-all duration-200"
                                >
                                    <div className={`shrink-0 w-10 h-10 rounded-xl ${c.bg} border ${c.border} flex items-center justify-center`}>
                                        <Icon className={`w-4 h-4 ${c.icon}`} />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-bold text-slate-900 text-[15px] mb-1">{t.title}</p>
                                        <p className="text-[13px] text-slate-500 leading-relaxed">{t.desc}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <p className="mt-5 text-xs text-slate-400">
                        Estructura vertical: la dirección y la ejecución comparten despacho. Sin subcontratas, sin
                        agencias intermedias, sin dilución de responsabilidad.
                    </p>
                </motion.div>

                {/* ── Stats ── */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-10"
                >
                    {stats.map(s => (
                        <div key={s.label} className="glass-card rounded-2xl py-6 px-4 text-center">
                            <span className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight block mb-1">{s.value}</span>
                            <span className="text-xs text-slate-400 font-mono tracking-wide">{s.label}</span>
                        </div>
                    ))}
                </motion.div>

                {/* ── CTA ── */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="flex flex-col sm:flex-row items-center justify-center gap-4"
                >
                    <a href="https://calendly.com/innovatheratech/30min" target="_blank" rel="noopener noreferrer"
                        className="group flex items-center gap-2.5 px-7 py-3.5 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition-all shadow-[0_4px_16px_rgba(37,99,235,0.3)] active:scale-95">
                        <Calendar className="w-4 h-4" />
                        Agenda una llamada con nosotros
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </a>
                    <span className="text-xs text-slate-400 font-mono uppercase tracking-widest">30 min · Gratuita · Sin compromiso</span>
                </motion.div>
            </div>
        </section>
    );
}
